/* Copyright (c) 2026 Dobler Data Solutions, LLC. All rights reserved. */

import { useState, useRef } from 'react';
import type { UserDto, UserRole, AdUserDto } from '@shared/types';
import { apiClient } from '../../services/apiClient';

interface Props {
  onClose: () => void;
  onAdded: (user: UserDto) => void;
}

const ROLE_OPTIONS: { label: string; value: UserRole }[] = [
  { label: 'Pharmacist', value: 'Pharmacist' },
  { label: 'Manager',    value: 'Manager' },
  { label: 'Admin',      value: 'Admin' },
];

export function AddUserModal({ onClose, onAdded }: Props) {
  const [query, setQuery]           = useState('');
  const [results, setResults]       = useState<AdUserDto[]>([]);
  const [selected, setSelected]     = useState<AdUserDto | null>(null);
  const [role, setRole]             = useState<UserRole>('Pharmacist');
  const [searching, setSearching]   = useState(false);
  const [saving, setSaving]         = useState(false);
  const [error, setError]           = useState<string | null>(null);
  const debounce = useRef<ReturnType<typeof setTimeout> | null>(null);

  function handleQueryChange(val: string) {
    setQuery(val);
    setSelected(null);
    setError(null);
    if (debounce.current) clearTimeout(debounce.current);
    if (val.trim().length < 2) { setResults([]); return; }
    debounce.current = setTimeout(() => searchAD(val.trim()), 350);
  }

  async function searchAD(q: string) {
    try {
      setSearching(true);
      const res = await apiClient.get<AdUserDto[]>(`/users/search-ad?q=${encodeURIComponent(q)}`);
      setResults(res.data);
    } catch {
      setResults([]);
    } finally {
      setSearching(false);
    }
  }

  function selectUser(u: AdUserDto) {
    setSelected(u);
    setQuery(u.displayName ? `${u.displayName} (${u.email})` : u.email);
    setResults([]);
  }

  async function handleAdd() {
    const email       = selected?.email || query.trim();
    const displayName = selected?.displayName || email;
    const oid         = selected?.oid || null;

    if (!email || !role) return;

    try {
      setSaving(true);
      setError(null);
      const res = await apiClient.post<UserDto>('/users', { email, displayName, oid, role });
      onAdded(res.data);
    } catch (err) {
      setError((err as Error).message || 'Failed to add user');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" onClick={onClose}>
      <div
        className="bg-white rounded-xl shadow-2xl w-full max-w-md p-6 flex flex-col gap-5"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold text-gray-900">Add User</h2>
          <button onClick={onClose} title="Close" className="text-gray-400 hover:text-gray-600 text-xl leading-none">&times;</button>
        </div>

        {/* Search */}
        <div className="relative">
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Search by name or email
          </label>
          <input
            type="text"
            value={query}
            onChange={(e) => handleQueryChange(e.target.value)}
            placeholder="e.g. John Smith or john@company.com"
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-dds-blue-500"
            autoFocus
          />
          {searching && (
            <p className="text-xs text-gray-400 mt-1">Searching directory…</p>
          )}
          {results.length > 0 && (
            <ul className="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-48 overflow-y-auto">
              {results.map((u) => (
                <li
                  key={u.oid || u.email}
                  className="px-3 py-2 text-sm hover:bg-dds-blue-50 cursor-pointer"
                  onClick={() => selectUser(u)}
                >
                  <span className="font-medium text-gray-900">{u.displayName}</span>
                  <span className="text-gray-400 ml-2 text-xs">{u.email}</span>
                </li>
              ))}
            </ul>
          )}
          {!searching && results.length === 0 && query.length >= 2 && !selected && (
            <p className="text-xs text-gray-400 mt-1">
              No directory matches — you can still add by email directly.
            </p>
          )}
        </div>

        {/* Role */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Role</label>
          <select
            value={role ?? ''}
            onChange={(e) => setRole(e.target.value as UserRole)}
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-dds-blue-500"
          >
            {ROLE_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value ?? ''}>{opt.label}</option>
            ))}
          </select>
          <p className="text-xs text-gray-400 mt-1">
            Pharmacist · browse &amp; assign&ensp;|&ensp;
            Manager · dashboard access&ensp;|&ensp;
            Admin · full access + user management
          </p>
        </div>

        {error && (
          <p className="text-sm text-red-600">{error}</p>
        )}

        {/* Actions */}
        <div className="flex gap-3 justify-end pt-1">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm text-gray-600 hover:text-gray-900 border border-gray-300 rounded-lg"
          >
            Cancel
          </button>
          <button
            onClick={handleAdd}
            disabled={saving || (!selected && query.trim().length < 3)}
            className="px-4 py-2 text-sm font-medium text-white bg-dds-blue-500 hover:bg-dds-blue-600 disabled:opacity-50 rounded-lg"
          >
            {saving ? 'Adding…' : 'Add User'}
          </button>
        </div>
      </div>
    </div>
  );
}
